<template>
  <q-page class="q-pa-xl centered-container">
    <div class="row full_width justify-center q-mt-lg">
      <div class="col-12 col-md-10">
        <div class="text-h5 q-mb-lg">
          {{ $page.props.i18n.summary }}
        </div>

        <!-- ========================================== -->
        <!-- CONTROL: 8€/mois/client                    -->
        <!-- ========================================== -->
        <q-card
          flat
          bordered
          class="q-mb-lg"
        >
          <q-card-section class="row items-center justify-between q-pb-none">
            <div class="text-h6">
              {{ $page.props.i18n.control_title }}
            </div>
            <q-badge
              v-if="$page.props.subscription"
              :color="statusColor($page.props.subscription.status)"
              :label="statusLabel($page.props.subscription.status)"
              class="text-caption"
            />
          </q-card-section>

          <q-card-section class="text-muted q-pt-sm q-pb-none">
            {{ $page.props.i18n.control_description }}
          </q-card-section>

          <q-card-section>
            <div class="row q-col-gutter-sm">
              <div
                v-for="(feature, i) in $page.props.control_features"
                :key="'cf-' + i"
                class="col-12 col-sm-6"
              >
                <div class="row items-center no-wrap q-gutter-xs">
                  <q-icon
                    name="las la-check"
                    color="positive"
                    size="16px"
                  />
                  <span class="text-body2">{{ feature }}</span>
                </div>
              </div>
            </div>
          </q-card-section>

          <q-separator />

          <!-- Not subscribed -->
          <q-card-section v-if="!$page.props.subscription">
            <div class="text-body1 q-mb-md">
              <q-chip
                dense
                color="pastel-purple"
                class="text-default"
              >
                {{ Math.max(wg.workspace_count, 1) }} client{{ Math.max(wg.workspace_count, 1) > 1 ? 's' : '' }}
                &times; 8&euro; = {{ Math.max(wg.workspace_count, 1) * 8 }}&euro; {{ $page.props.i18n.price_per_month }}
              </q-chip>
            </div>
            <q-banner
              dense
              class="bg-pastel-blue q-mb-md"
              style="max-width: fit-content"
            >
              <template #avatar>
                <q-icon
                  name="las la-info-circle"
                  size="sm"
                />
              </template>
              {{ $page.props.i18n.needs_subscription }}
            </q-banner>
            <q-btn
              color="primary"
              unelevated
              :label="$page.props.i18n.subscribe_control"
              :loading="controlLoading"
              @click="subscribeControl"
            />
          </q-card-section>

          <!-- Subscribed -->
          <q-card-section v-else>
            <div class="row items-center q-gutter-md q-mb-md">
              <q-chip
                dense
                color="pastel-green"
                class="text-default"
              >
                {{ $page.props.subscription.price }}&euro; {{ $page.props.i18n.price_per_client }}
                &times; {{ wg.workspace_count }}
                = {{ controlTotal }}&euro; {{ $page.props.i18n.price_per_month }}
              </q-chip>
              <span
                v-if="$page.props.subscription.renew_at"
                class="text-caption text-muted"
              >
                {{ $page.props.i18n.next_renewal }} : {{ formatDate($page.props.subscription.renew_at) }}
              </span>
            </div>
            <div class="row q-gutter-sm">
              <q-btn
                v-if="$page.props.subscription.status === 'active' && $page.props.subscription.renew_at"
                outline
                dense
                color="negative"
                size="sm"
                :label="$page.props.i18n.cancel_renewal"
                :loading="cancelLoading"
                @click="cancelRenewal"
              />
              <q-btn
                v-if="$page.props.subscription.status === 'active' && !$page.props.subscription.renew_at"
                outline
                dense
                color="positive"
                size="sm"
                :label="$page.props.i18n.renew_subscription"
                :loading="renewLoading"
                @click="renewSubscription"
              />
              <q-btn
                outline
                dense
                size="sm"
                :label="$page.props.i18n.payment_portal"
                :loading="portalLoading"
                @click="openPortal"
              />
            </div>
          </q-card-section>
        </q-card>

        <!-- ========================================== -->
        <!-- SUPPORT: 66€/mois forfait                  -->
        <!-- ========================================== -->
        <q-card
          flat
          bordered
          class="q-mb-lg"
        >
          <q-card-section class="row items-center justify-between q-pb-none">
            <div class="text-h6">
              {{ $page.props.i18n.support_title }}
            </div>
            <q-badge
              v-if="wg.support_active"
              :color="statusColor($page.props.support_subscription?.status)"
              :label="statusLabel($page.props.support_subscription?.status)"
              class="text-caption"
            />
            <q-badge
              v-else
              color="grey-4"
              text-color="grey-8"
              :label="$page.props.i18n.inactive"
              class="text-caption"
            />
          </q-card-section>

          <q-card-section class="text-muted q-pt-sm q-pb-none">
            {{ $page.props.i18n.support_description }}
          </q-card-section>

          <q-card-section>
            <div class="row q-col-gutter-sm">
              <div
                v-for="(feature, i) in $page.props.support_features"
                :key="'sf-' + i"
                class="col-12 col-sm-6"
              >
                <div class="row items-center no-wrap q-gutter-xs">
                  <q-icon
                    name="las la-check"
                    color="positive"
                    size="16px"
                  />
                  <span class="text-body2">{{ feature }}</span>
                </div>
              </div>
            </div>
          </q-card-section>

          <q-separator />

          <!-- Not subscribed to Support -->
          <q-card-section v-if="!wg.support_active">
            <q-btn
              color="primary"
              unelevated
              :label="$page.props.i18n.subscribe_support"
              :loading="supportLoading"
              :disable="!$page.props.subscription"
              @click="subscribeSupport"
            />
            <div
              v-if="!$page.props.subscription"
              class="text-caption text-muted q-mt-sm"
            >
              {{ $page.props.i18n.needs_subscription }}
            </div>
          </q-card-section>

          <!-- Subscribed to Support: Calendly links -->
          <q-card-section v-else>
            <div class="text-subtitle2 q-mb-md">
              Calendly
            </div>

            <!-- 1h personnalisation plan comptable -->
            <div class="q-mb-sm">
              <div
                v-if="wg.support_plan_comptable_done"
                class="row items-center q-gutter-xs text-muted"
              >
                <q-icon
                  name="las la-check-circle"
                  color="positive"
                  size="20px"
                />
                <span>{{ $page.props.i18n.calendly_plan_comptable }} — {{ $page.props.i18n.done }}</span>
              </div>
              <q-btn
                v-else
                outline
                dense
                color="primary"
                icon="las la-calendar"
                :label="$page.props.i18n.calendly_plan_comptable"
                type="a"
                :href="$page.props.calendly_links.plan_comptable"
                target="_blank"
              />
            </div>

            <!-- 1h explication outils -->
            <div class="q-mb-sm">
              <div
                v-if="wg.support_outils_done"
                class="row items-center q-gutter-xs text-muted"
              >
                <q-icon
                  name="las la-check-circle"
                  color="positive"
                  size="20px"
                />
                <span>{{ $page.props.i18n.calendly_outils }} — {{ $page.props.i18n.done }}</span>
              </div>
              <q-btn
                v-else
                outline
                dense
                color="primary"
                icon="las la-calendar"
                :label="$page.props.i18n.calendly_outils"
                type="a"
                :href="$page.props.calendly_links.outils"
                target="_blank"
              />
            </div>

            <q-separator class="q-my-md" />

            <div class="row q-gutter-sm">
              <q-btn
                v-if="$page.props.support_subscription?.status === 'active' && $page.props.support_subscription?.renew_at"
                outline
                dense
                color="negative"
                size="sm"
                :label="$page.props.i18n.cancel_renewal"
                :loading="cancelSupportLoading"
                @click="cancelSupportRenewal"
              />
              <q-btn
                v-if="$page.props.support_subscription?.status === 'active' && !$page.props.support_subscription?.renew_at"
                outline
                dense
                color="positive"
                size="sm"
                :label="$page.props.i18n.renew_subscription"
                :loading="renewSupportLoading"
                @click="renewSupportSubscription"
              />
            </div>
          </q-card-section>
        </q-card>

        <!-- ========================================== -->
        <!-- HEURES PREMIUM: 200€/h                     -->
        <!-- ========================================== -->
        <q-card
          flat
          bordered
        >
          <q-card-section class="q-pb-none">
            <div class="text-h6">
              {{ $page.props.i18n.premium_title }}
            </div>
          </q-card-section>

          <q-card-section class="text-muted q-pt-sm">
            {{ $page.props.i18n.premium_description }}
          </q-card-section>

          <q-separator />

          <q-card-section>
            <div class="row items-center q-gutter-lg">
              <q-btn
                color="primary"
                unelevated
                :label="$page.props.i18n.buy_hour"
                :loading="buyHourLoading"
                :disable="!$page.props.subscription"
                @click="buyHours"
              />
            </div>
            <div
              v-if="!$page.props.subscription"
              class="text-caption text-muted q-mt-sm"
            >
              {{ $page.props.i18n.needs_subscription }}
            </div>

            <!-- Calendly verif dossier -->
            <q-separator class="q-my-md" />
            <div
              v-if="wg.support_half_hours_balance > 0"
              class="row items-center q-gutter-sm"
            >
              <q-btn
                outline
                dense
                color="primary"
                icon="las la-calendar"
                :label="$page.props.i18n.calendly_verif"
                type="a"
                :href="$page.props.calendly_links.verif_dossier"
                target="_blank"
              />
              <q-chip
                dense
                color="pastel-green"
                class="text-default"
              >
                {{ wg.support_half_hours_balance }} {{ $page.props.i18n.half_hours_unit }}
              </q-chip>
            </div>
            <div
              v-else
              class="row items-center q-gutter-xs text-muted"
            >
              <q-icon
                name="las la-clock"
                size="20px"
              />
              <span>{{ $page.props.i18n.calendly_verif }} — 0 {{ $page.props.i18n.half_hours_unit }}</span>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </q-page>
</template>

<script>
import Layout from '@/layouts/wg_admin.vue';
import { router } from '@inertiajs/vue3';
import { rails } from '@/entrypoints/rails.js';

export default {
  layout: Layout,
  data() {
    return {
      controlLoading: false,
      supportLoading: false,
      buyHourLoading: false,
      cancelLoading: false,
      renewLoading: false,
      cancelSupportLoading: false,
      renewSupportLoading: false,
      portalLoading: false,
    };
  },
  computed: {
    wg() {
      return this.$page.props.workspace_group;
    },
    controlTotal() {
      const sub = this.$page.props.subscription;
      if (!sub) return 0;
      return (sub.price * this.wg.workspace_count).toFixed(2);
    },
  },
  methods: {
    statusColor(status) {
      if (status === 'active') return 'positive';
      if (status === 'past_due') return 'warning';
      return 'negative';
    },
    statusLabel(status) {
      const i18n = this.$page.props.i18n;
      const labels = {
        active: i18n.status_active,
        paused: i18n.status_paused,
        canceled: i18n.status_canceled,
        unpaid: i18n.status_unpaid,
        past_due: i18n.status_past_due,
        incomplete: i18n.status_incomplete,
        trialing: i18n.status_trialing,
      };
      return labels[status] || status || i18n.inactive;
    },
    async postAction(url) {
      const response = await rails.post(url);
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    },
    async subscribeControl() {
      this.controlLoading = true;
      try {
        await this.postAction(`/wg_admin/${this.wg.id}/subscriptions`);
      } catch (e) {
        this.controlLoading = false;
        console.error(e);
      }
    },
    async subscribeSupport() {
      this.supportLoading = true;
      try {
        await this.postAction(`/wg_admin/${this.wg.id}/subscriptions/subscribe_support`);
      } catch (e) {
        this.supportLoading = false;
        console.error(e);
      }
    },
    async buyHours() {
      this.buyHourLoading = true;
      try {
        await this.postAction(`/wg_admin/${this.wg.id}/subscriptions/buy_hours`);
      } catch (e) {
        this.buyHourLoading = false;
        console.error(e);
      }
    },
    cancelRenewal() {
      this.cancelLoading = true;
      const subId = this.$page.props.subscription.id;
      router.patch(`/wg_admin/${this.wg.id}/subscriptions/${subId}`, {
        end_of_period: 'cancel',
      }, {
        onFinish: () => { this.cancelLoading = false; },
      });
    },
    renewSubscription() {
      this.renewLoading = true;
      const subId = this.$page.props.subscription.id;
      router.patch(`/wg_admin/${this.wg.id}/subscriptions/${subId}`, {
        end_of_period: 'renew',
      }, {
        onFinish: () => { this.renewLoading = false; },
      });
    },
    cancelSupportRenewal() {
      this.cancelSupportLoading = true;
      const subId = this.$page.props.support_subscription.id;
      router.patch(`/wg_admin/${this.wg.id}/subscriptions/${subId}`, {
        end_of_period: 'cancel',
        type: 'support',
      }, {
        onFinish: () => { this.cancelSupportLoading = false; },
      });
    },
    renewSupportSubscription() {
      this.renewSupportLoading = true;
      const subId = this.$page.props.support_subscription.id;
      router.patch(`/wg_admin/${this.wg.id}/subscriptions/${subId}`, {
        end_of_period: 'renew',
        type: 'support',
      }, {
        onFinish: () => { this.renewSupportLoading = false; },
      });
    },
    async openPortal() {
      this.portalLoading = true;
      try {
        await this.postAction(`/wg_admin/${this.wg.id}/subscriptions/portal`);
      } catch (e) {
        this.portalLoading = false;
        console.error(e);
      }
    },
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleDateString(this.$page.props.auth?.locale || 'fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
  },
};
</script>
